// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { epsgCheckTimeoutMs } from './constants';

export type EpsgFileState = 'valid' | 'missing' | 'invalid' | 'checking' | 'failed';

// Minimal shape shared by ILasFile, ITifFile, and the panel row projections.
export interface EpsgFileStateInput {
  epsg: number;
  epsgCheckStatus?: EpsgCheckStatus;
  createdAt?: string | number | Date;
}

/**
 * Display state for a las/tif file's EPSG check.
 *
 * A resolved EPSG always wins no matter what the status field claims, so a late-arriving
 * check result self-heals every other state. Files that predate the epsgCheckStatus field
 * carry no status — for them the stored epsg is the final word (pre-AUT-6373 behavior).
 * A pending check with a missing or unparseable createdAt cannot age out; it stays
 * 'checking' rather than failing fast, and the project poll keeps re-evaluating.
 */
export const getEpsgFileState = (file: EpsgFileStateInput, nowMs: number): EpsgFileState => {
  if (file.epsg > 0) {
    return 'valid';
  }
  if (file.epsgCheckStatus === 'failed') {
    return 'failed';
  }
  if (file.epsgCheckStatus === 'pending') {
    const createdAtMs = file.createdAt !== undefined ? new Date(file.createdAt).getTime() : NaN;
    if (Number.isFinite(createdAtMs) && nowMs - createdAtMs > epsgCheckTimeoutMs) {
      return 'failed';
    }
    return 'checking';
  }
  if (file.epsg === -2) {
    return 'missing';
  }
  return 'invalid';
};

// Whether any of the files has passed its EPSG check: what an own-data hold needs before it reads "Data received".
// A resolved EPSG does not age, so the time passed in does not change the answer.
export const hasCheckedFile = (files: EpsgFileStateInput[] | undefined, nowMs: number): boolean =>
  !!files?.some((file) => getEpsgFileState(file, nowMs) === 'valid');

const nonValidLabels: Record<Exclude<EpsgFileState, 'valid'>, string> = {
  checking: 'checking…',
  failed: 'check failed',
  missing: 'missing',
  invalid: 'invalid',
};

// Label for the ` - [ EPSG: … ]` row template shared by the customer panel and the admin
// project page. verticalEpsg is only meaningful alongside a resolved horizontal EPSG, so
// it is appended to the valid label only.
export const formatEpsgLabel = (file: EpsgFileStateInput & { verticalEpsg?: number }, nowMs: number): string => {
  const state = getEpsgFileState(file, nowMs);
  if (state === 'valid') {
    return `${file.epsg}${file.verticalEpsg ? ` + ${file.verticalEpsg}` : ''}`;
  }
  return nonValidLabels[state];
};
